// hooks/useAllowedSourceTypes.ts
/**
 * Hook that resolves whether a source-type is activatable for the
 * currently active account.
 *
 * Reads from `useAuth().activeAccount`, which AuthContext rebuilds from
 * the session response: on page load (GET /auth/me), on account switch
 * (POST /auth/switch-account), and on the ~55-minute proactive refresh
 * (POST /auth/refresh). Nothing pushes a change into an open tab, so a
 * SuperAdmin edit to the allowlist does NOT reach a customer who is
 * already looking at the page. They see it at their next page load,
 * sign-in or session refresh.
 *
 * Note: there is **no** superadmin bypass here. The allowlist is the
 * single source of truth — to activate a type for a customer, the
 * superadmin must first add it to that account's allowlist via
 * SuperAdmin → Clients → Data Sources. This matches the backend guard
 * (assert_source_type_allowed has no role-based bypass either).
 *
 * Returns:
 * - `isAllowed(typeKey)` — true when no restriction is set and the type is
 *   not a preview type, OR the type is in the allowlist (ADR-0313).
 * - `allowlist` — raw value from context.
 * - `hasRestriction` — convenience boolean. Use for empty-state and
 *   banner gating instead of `allowlist?.includes(...)` to avoid
 *   null-pointer foot-guns.
 */

import { useMemo } from "react";
import { isSourceTypeAllowed } from "@/components/data-sources/registry-mapping";
import { useAuth } from "@/context/AuthContext";

export function useAllowedSourceTypes() {
  const { activeAccount } = useAuth();

  const allowlist: string[] | null = activeAccount?.allowedSourceTypes ?? null;

  return useMemo(() => {
    function isAllowed(typeKey: string): boolean {
      return isSourceTypeAllowed(allowlist, typeKey);
    }

    return {
      isAllowed,
      allowlist,
      hasRestriction: allowlist !== null,
    };
  }, [allowlist]);
}
